/**
 * Actor stat resolution: resolve(stat) = (base + Σflat) × Π(1 + pct), cached,
 * invalidated on modifier change. Artifact boons and (later) curses are modifiers
 * keyed by source so unequipping removes them cleanly.
 */

export type StatId =
  | 'damageMult' // multiplies ability damage (base 1)
  | 'cooldownMult' // multiplies cooldowns — lower is faster (base 1)
  | 'areaMult' // multiplies radii (base 1)
  | 'projSpeedMult' // player projectile speed (base 1)
  | 'moveSpeed' // px/s (base 260)
  | 'maxHp' // hearts (base 6)
  | 'gemGainMult' // gem value multiplier (base 1)
  | 'magnetRange' // gem pickup radius (base 85)
  | 'killFillMult' // kill-bar fill multiplier (base 1; meta Zeal track)
  | 'pickupRateMult'; // pickup-track attunement (base 1; meta Communion track)

const BASE: Record<StatId, number> = {
  damageMult: 1,
  cooldownMult: 1,
  areaMult: 1,
  projSpeedMult: 1,
  moveSpeed: 260,
  maxHp: 6,
  gemGainMult: 1,
  magnetRange: 85,
  killFillMult: 1,
  pickupRateMult: 1,
};

export interface StatModifier {
  stat: StatId;
  flat?: number;
  pct?: number; // +0.3 = +30%
}

export class StatSheet {
  private mods: (StatModifier & { source: string })[] = [];
  private cache = new Map<StatId, number>();

  addModifiers(source: string, mods: StatModifier[]): void {
    for (const m of mods) this.mods.push({ ...m, source });
    this.cache.clear();
  }

  removeSource(source: string): void {
    this.mods = this.mods.filter((m) => m.source !== source);
    this.cache.clear();
  }

  resolve(stat: StatId): number {
    const cached = this.cache.get(stat);
    if (cached !== undefined) return cached;
    let flat = BASE[stat];
    let mult = 1;
    for (const m of this.mods) {
      if (m.stat !== stat) continue;
      flat += m.flat ?? 0;
      mult *= 1 + (m.pct ?? 0);
    }
    const v = flat * mult;
    this.cache.set(stat, v);
    return v;
  }
}
